/**
 * Cross-tenant ASCII animation player for presentation surfaces (landing heroes, marketing, etc.).
 * Frames are pre-generated from video and served as static assets under `labs/public/`.
 *
 * For a single static frame, see `landing-ascii-visual.tsx` (`/ascii/{artId}.txt`).
 */

import { useEffect, useMemo, useRef, useState } from "react"

type Quality = "low" | "medium" | "high"

interface ColorAsciiMeta {
  version: 2
  format: "color-ascii-v2"
  width: number
  height: number
  fps: number
  frameCount: number
  palette: string[]
  paletteSize: number
  charset: string
  renderMode: "ascii" | "katakana" | "katakana-full" | "katakana-lines"
  colorMode: "color"
  bgMode: "black" | "green" | "transparent"
  dithering: "off" | "ordered"
  frameEncoding: "packed-12"
  bitsPerGlyph: number
  bitsPerColor: number
  bytesPerFrame: number
}

interface ASCIIAnimationProps {
  frames?: string[]
  className?: string
  fps?: number
  frameCount?: number
  frameFolder?: string
  textSize?: string
  showFrameCounter?: boolean
  quality?: Quality
  ariaLabel?: string
  lazy?: boolean
  color?: string
  gradient?: string
  playOnHover?: boolean
  /** `contain` scales down to fit (default); `cover` fills the parent and may crop */
  fit?: 'contain' | 'cover'
}

const FALLBACK_ORDER: Record<Quality, Quality[]> = {
  low: ["low", "high", "medium"],
  medium: ["medium", "high", "low"],
  high: ["high", "low", "medium"],
}

const FONT_SIZE = 10
const FONT_FAMILY = "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace"
const CJK_FONT_FAMILY = '"Noto Sans JP", "IPAGothic", sans-serif'

function usesCjkRenderMode(meta: ColorAsciiMeta | null) {
  return meta?.renderMode === "katakana-full" || meta?.renderMode === "katakana-lines"
}

function fontFamilyForMeta(meta: ColorAsciiMeta | null) {
  return usesCjkRenderMode(meta) ? CJK_FONT_FAMILY : FONT_FAMILY
}

function measureCharForMeta(meta: ColorAsciiMeta | null) {
  return usesCjkRenderMode(meta) ? "ア" : "M"
}
/** Avoid saturating the browser connection pool (many hosts cap ~6 per origin). */
const COLOR_FRAME_FETCH_BATCH = 24

async function resolveFrameSource(frameFolder: string, quality: Quality, firstFrameFile: string) {
  const fallbackQualities = FALLBACK_ORDER[quality]

  for (const candidate of fallbackQualities) {
    try {
      const metaUrl = `/${frameFolder}/${candidate}/meta.json`
      const metaResponse = await fetch(metaUrl)
      if (metaResponse.ok) {
        return { baseUrl: `/${frameFolder}/${candidate}`, format: "color" as const }
      }
    } catch {
      // continue
    }

    try {
      const probeUrl = `/${frameFolder}/${candidate}/${firstFrameFile}`
      const probeResponse = await fetch(probeUrl)
      if (probeResponse.ok) {
        return { baseUrl: `/${frameFolder}/${candidate}`, format: "text" as const }
      }
    } catch {
      // continue
    }
  }

  return null
}

function decodeColorFrame(meta: ColorAsciiMeta, buffer: Uint8Array) {
  const cellCount = meta.width * meta.height
  const glyphs = new Uint8Array(cellCount)
  const colors = new Uint8Array(cellCount)

  for (let i = 0, offset = 0; i < cellCount; i += 2, offset += 3) {
    const byte0 = buffer[offset] ?? 0
    const byte1 = buffer[offset + 1] ?? 0
    const byte2 = buffer[offset + 2] ?? 0
    const cellA = (byte0 << 4) | (byte1 >> 4)
    const cellB = ((byte1 & 0x0f) << 8) | byte2

    glyphs[i] = cellA >> meta.bitsPerColor
    colors[i] = cellA & ((1 << meta.bitsPerColor) - 1)

    if (i + 1 < cellCount) {
      glyphs[i + 1] = cellB >> meta.bitsPerColor
      colors[i + 1] = cellB & ((1 << meta.bitsPerColor) - 1)
    }
  }

  return {
    glyphs,
    colors,
  }
}

function drawColorFrame(canvas: HTMLCanvasElement, meta: ColorAsciiMeta, buffer: Uint8Array) {
  const ctx = canvas.getContext("2d")
  if (!ctx) return

  const family = fontFamilyForMeta(meta)
  ctx.font = `${FONT_SIZE}px ${family}`
  ctx.textBaseline = "top"
  const cellWidth = Math.ceil(ctx.measureText(measureCharForMeta(meta)).width)
  const cellHeight = Math.ceil(FONT_SIZE)

  canvas.width = Math.max(1, meta.width * cellWidth)
  canvas.height = Math.max(1, meta.height * cellHeight)

  ctx.clearRect(0, 0, canvas.width, canvas.height)
  if (meta.bgMode !== "transparent") {
    ctx.fillStyle = "#000"
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }
  ctx.font = `${FONT_SIZE}px ${family}`
  ctx.textBaseline = "top"

  const frame = decodeColorFrame(meta, buffer)
  for (let i = 0; i < frame.glyphs.length; i++) {
    const char = meta.charset[frame.glyphs[i]] ?? " "
    if (char === " ") continue
    ctx.fillStyle = meta.palette[frame.colors[i]] ?? "#fff"
    ctx.fillText(char, (i % meta.width) * cellWidth, Math.floor(i / meta.width) * cellHeight)
  }
}

function ASCIIAnimation({
  frames: providedFrames,
  className = "",
  fps = 24,
  frameCount = 60,
  frameFolder = "frames",
  textSize = "text-xs",
  showFrameCounter = false,
  ariaLabel,
  quality = "medium",
  lazy = true,
  color,
  gradient,
  playOnHover = false,
  fit = 'contain',
}: ASCIIAnimationProps) {
  const [frames, setFrames] = useState<string[]>([])
  const [colorFrames, setColorFrames] = useState<Uint8Array[]>([])
  const [meta, setMeta] = useState<ColorAsciiMeta | null>(null)
  const [format, setFormat] = useState<"text" | "color" | null>(providedFrames ? "text" : null)
  const [currentFrame, setCurrentFrame] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [scale, setScale] = useState(1)
  const containerRef = useRef<HTMLDivElement>(null)
  const preRef = useRef<HTMLPreElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const sourceRef = useRef<{ baseUrl: string; format: "text" | "color" } | null>(null)
  const fullLoadTriggered = useRef(false)
  const fullLoadInFlight = useRef(false)
  const [sourceReady, setSourceReady] = useState(false)

  const frameFiles = useMemo(
    () => Array.from({ length: frameCount }, (_, i) => `frame_${String(i + 1).padStart(5, "0")}.txt`),
    [frameCount]
  )

  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    const updateVisibility = (intersecting: boolean) => {
      setIsVisible(intersecting)
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => updateVisibility(entry.isIntersecting))
      },
      { threshold: 0.1, rootMargin: "50px" }
    )
    observer.observe(node)

    // Hero panels are often in view on first paint before the observer fires.
    const rect = node.getBoundingClientRect()
    const inView =
      rect.bottom > 0 &&
      rect.top < window.innerHeight &&
      rect.right > 0 &&
      rect.left < window.innerWidth
    if (inView) updateVisibility(true)

    return () => observer.disconnect()
  }, [format])

  useEffect(() => {
    if (providedFrames) {
      setFrames(providedFrames)
      return
    }

    let cancelled = false
    fullLoadTriggered.current = false
    fullLoadInFlight.current = false
    setSourceReady(false)

    const loadPreview = async () => {
      const source = await resolveFrameSource(frameFolder, quality, frameFiles[0])
      if (!source || cancelled) return

      sourceRef.current = source
      setFormat(source.format)

      if (source.format === "color") {
        const metaResponse = await fetch(`${source.baseUrl}/meta.json`)
        const resolvedMeta = await metaResponse.json()
        if (cancelled) return
        setMeta(resolvedMeta)

        const frameResponse = await fetch(`${source.baseUrl}/frame_00001.bin`)
        const buffer = new Uint8Array(await frameResponse.arrayBuffer())
        if (cancelled) return
        setColorFrames([buffer])
      } else {
        const response = await fetch(`${source.baseUrl}/${frameFiles[0]}`)
        const firstFrame = await response.text()
        if (cancelled) return
        setFrames([firstFrame])
      }

      if (!lazy) {
        await loadAllFrames(source)
      } else if (!cancelled) {
        setSourceReady(true)
      }
    }

    loadPreview()
    return () => {
      cancelled = true
    }
  }, [frameFiles, frameFolder, lazy, providedFrames, quality])

  const loadAllFrames = async (resolvedSource = sourceRef.current) => {
    if (!resolvedSource || fullLoadTriggered.current || fullLoadInFlight.current) return
    fullLoadInFlight.current = true

    try {
      if (resolvedSource.format === "color") {
        const metaResponse = await fetch(`${resolvedSource.baseUrl}/meta.json`)
        if (!metaResponse.ok) throw new Error("meta.json fetch failed")
        const resolvedMeta: ColorAsciiMeta = await metaResponse.json()
        setMeta(resolvedMeta)

        const count = resolvedMeta.frameCount
        const loaded: Uint8Array[] = []

        for (let start = 0; start < count; start += COLOR_FRAME_FETCH_BATCH) {
          const end = Math.min(start + COLOR_FRAME_FETCH_BATCH, count)
          const batch = await Promise.all(
            Array.from({ length: end - start }, async (_, offset) => {
              const index = start + offset + 1
              const response = await fetch(
                `${resolvedSource.baseUrl}/frame_${String(index).padStart(5, "0")}.bin`
              )
              if (!response.ok) throw new Error(`frame ${index} fetch failed`)
              return new Uint8Array(await response.arrayBuffer())
            })
          )
          loaded.push(...batch)
          // Start playback as soon as we have a few frames (not only after all 664).
          if (loaded.length >= 2) {
            setColorFrames([...loaded])
          }
        }

        setColorFrames(loaded)
        fullLoadTriggered.current = true
      } else {
        const loaded: string[] = []
        for (let start = 0; start < frameFiles.length; start += COLOR_FRAME_FETCH_BATCH) {
          const slice = frameFiles.slice(start, start + COLOR_FRAME_FETCH_BATCH)
          const batch = await Promise.all(
            slice.map(async filename => {
              const response = await fetch(`${resolvedSource.baseUrl}/${filename}`)
              if (!response.ok) throw new Error(`${filename} fetch failed`)
              return response.text()
            })
          )
          loaded.push(...batch)
          if (loaded.length >= 2) setFrames([...loaded])
        }
        setFrames(loaded)
        fullLoadTriggered.current = true
      }
    } catch {
      fullLoadTriggered.current = false
    } finally {
      fullLoadInFlight.current = false
    }
  }

  const shouldPlay = isVisible && (!playOnHover || isHovered)

  useEffect(() => {
    if (shouldPlay && lazy && sourceReady && !fullLoadTriggered.current) {
      void loadAllFrames()
    }
  }, [lazy, shouldPlay, sourceReady])

  useEffect(() => {
    if (!shouldPlay || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    const total = format === "color" ? colorFrames.length : frames.length
    if (total <= 1) return

    const interval = window.setInterval(() => {
      setCurrentFrame(prev => (prev + 1) % total)
    }, 1000 / fps)

    return () => window.clearInterval(interval)
  }, [colorFrames.length, format, fps, frames.length, shouldPlay])

  useEffect(() => {
    if (format === "color" && meta && colorFrames[currentFrame] && canvasRef.current) {
      drawColorFrame(canvasRef.current, meta, colorFrames[currentFrame])
    }
  }, [colorFrames, currentFrame, format, meta])

  const currentTextFrame = frames[currentFrame] || frames[0] || ""
  const frameCounterTotal = format === "color" ? (meta?.frameCount || colorFrames.length) : (frames.length || frameCount)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateScale = () => {
      const target = format === "color" ? canvasRef.current : preRef.current
      if (!target) return

      const contentWidth = target.scrollWidth || target.clientWidth
      const contentHeight = target.scrollHeight || target.clientHeight
      if (!contentWidth || !contentHeight) return

      const scaleX = container.clientWidth / contentWidth
      const scaleY = container.clientHeight / contentHeight
      const nextScale =
        fit === 'cover'
          ? Math.max(scaleX, scaleY)
          : Math.min(scaleX, scaleY, 1)
      setScale(Number.isFinite(nextScale) ? nextScale : 1)
    }

    updateScale()
    const observer = new ResizeObserver(updateScale)
    observer.observe(container)
    if (preRef.current) observer.observe(preRef.current)
    if (canvasRef.current) observer.observe(canvasRef.current)
    return () => observer.disconnect()
  }, [currentTextFrame, currentFrame, fit, format, meta])

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full flex items-center justify-center overflow-hidden ${className}`}
      aria-label={ariaLabel}
      role={ariaLabel ? "img" : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {format === "color" ? (
        <canvas
          ref={canvasRef}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "center",
          }}
        />
      ) : (
        <pre
          ref={preRef}
          className={`${textSize} leading-none whitespace-pre select-none`}
          style={{
            margin: 0,
            padding: 0,
            color,
            backgroundImage: gradient,
            backgroundClip: gradient ? "text" : undefined,
            WebkitBackgroundClip: gradient ? "text" : undefined,
            WebkitTextFillColor: gradient ? "transparent" : undefined,
            fontFamily: FONT_FAMILY,
            transform: `scale(${scale})`,
            transformOrigin: "center",
          }}
        >
          {currentTextFrame}
        </pre>
      )}

      {showFrameCounter && (
        <div className="absolute bottom-2 right-2 rounded bg-black/60 px-2 py-1 text-[10px] font-mono text-white">
          Frame: {Math.min(currentFrame + 1, frameCounterTotal)}/{frameCounterTotal}
        </div>
      )}
    </div>
  )
}

export default ASCIIAnimation
export { ASCIIAnimation }
